import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import { ServiceOrderRequest, postContactUsCreateServiceRequest } from "api"
import ContactUsState from "components/ContactUs/ContactUsState"
import {
  StyledFiedlset,
  StyledLegend,
} from "components/ContactUs/CustomerService"
import { createRef, useEffect, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import { schemaCommon } from "reusable/Form/FormSchema"
import { ButtonWithArrow, NewText } from "ui"
import { getSubjectId, getUtmParams } from "utils"
import * as yup from "yup"
import AutocompleteCities from "./AutocompleteCities"
import FormInput from "./PazomatFormInput"

const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable. otherwise the contact us form will not work.")
}

const StyledFormDiv = styled.div`
  height: 100%;
  form {
    display: flex;
    flex-direction: column;
    .MuiAutocomplete-endAdornment {
      top: unset;
      ${(props) => props.theme.breakpoints.up("md")} {
        transform: none;
      }
    }
  }
`

const Asterisk = styled.span<{ $marginBottom?: number }>`
  color: blue;
  margin-bottom: ${(p) => (p.$marginBottom ? `${p.$marginBottom}px` : "0px")};
  width: 4px;
`

const StyledTextWrapper = styled.div`
  height: 44px;
  display: flex;
  align-items: center;
  padding-top: 52px;
  ${(props) => props.theme.breakpoints.up("sm")} {
    padding-top: 42px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: 24px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    padding-top: 42px;
  }
  p {
    margin-left: 8px;
  }
`
const StyledButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 60px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 50px;
  }
`
const schema = yup.object({
  firstName: schemaCommon.firstName,
  lastName: schemaCommon.lastName,
  ID: schemaCommon.IndividualID,
  phoneNumber: schemaCommon.phoneNumber,
  city: schemaCommon.city,
})

interface IFormInputs {
  firstName: string
  lastName: string
  ID: string
  phoneNumber: string
  city: string
}

function PazomatForm() {
  const [isSuccess, setIsSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const reCaptchaRef = createRef<ReCAPTCHA>()
  const [clearCity, setClearCity] = useState(false)

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    trigger,
    reset
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      ID: "",
      phoneNumber: "",
      city: "",
    },

    resolver: yupResolver(schema),
  })

  const onSubmit: SubmitHandler<IFormInputs> = async (data) => {
    setIsLoading(true)
    reCaptchaRef?.current?.reset()
    const recaptchaResponse =
      (await reCaptchaRef?.current?.executeAsync()) as string

    const postData: ServiceOrderRequest = {
      FirstName: data.firstName,
      LastName: data.lastName,
      MobilePhone: data.phoneNumber,
      Email: "notavailible@gmail.com",
      SubjectId: getSubjectId("הצטרפות"),
      Message: "N/A",
      IsFromYellowBox: false,
      CustomerId: data.ID,
      City: data.city,
      CustomerTypeId: "",
      PlatformUserId: "",
      GRecaptchaResponse: recaptchaResponse,
      ...getUtmParams(),
      Source: "10",
    }
    try {
      const response = (await postContactUsCreateServiceRequest(
        "",
        postData,
      )) as any
      if (response?.data?.Body && !response.data.errorCode) {
        setIsOpen(true)
        setIsSuccess(true)
        setClearCity(true)
        reset();
      } else {
        setIsOpen(true)
        setIsSuccess(false)
      }
    } catch (error) {
      setIsOpen(true)
      setIsSuccess(false)
    } finally {
      setIsLoading(false)
    }
  }
  const handleAutocompleteChange = (value: any) => {
    setValue("city", value)
    trigger("city")
  }
  useEffect(() => {
    const firstErrorKey = Object.keys(errors).find((key) => key in errors) as
      | keyof IFormInputs
      | undefined

    if (firstErrorKey) {
      const errorElement = document.querySelector(
        `input[name="${firstErrorKey}"]`,
      ) as HTMLInputElement | null

      errorElement?.focus()
    }
  }, [errors])

  return (
    <>
      <StyledFormDiv>
        <form onSubmit={handleSubmit(onSubmit)} style={{ height: "100%" }}>
          <StyledFiedlset>
            <StyledLegend>רוצה לשמוע עוד על פזומט?</StyledLegend>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <FormInput
                  field={field}
                  error={errors.firstName?.message}
                  placeholder="שם פרטי"
                  type="text"
                  required={true}
                  inputProps={{ fullWidth: true, style: { width: "100%" } }}
                  fullWidth
                />
              )}
            />

            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <FormInput
                  field={field}
                  error={errors.lastName?.message}
                  placeholder="שם משפחה"
                  type="text"
                  required={true}
                  fullWidth
                />
              )}
            />

            <Controller
              name="ID"
              control={control}
              render={({ field }) => (
                <FormInput
                  field={field}
                  error={errors.ID?.message}
                  placeholder="ת.ז / ח.פ"
                  type="number"
                  required={true}
                  fullWidth
                />
              )}
            />
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <AutocompleteCities
                  placeholder="עיר / יישוב"
                  field={field}
                  error={errors.city?.message}
                  required={true}
                  handleAutocompleteChange={handleAutocompleteChange}
                  clearFlag={{ value: clearCity, setter: setClearCity }}
                />
              )}
            />
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <FormInput
                  field={field}
                  error={errors.phoneNumber?.message}
                  placeholder="טלפון נייד"
                  type="number"
                  required={true}
                  fullWidth
                />
              )}
            />

            <StyledTextWrapper>
              <Asterisk>*</Asterisk>
              <NewText
                component={"p"}
                largeDesktopFontSize={16}
                largeDesktopLineHeight={"24px"}
                desktopFontWeight={400}
                desktopFontSize={12}
                desktopLineHeight="18px"
                mobileFontSize={16}
                mobileLineHeight={"24px"}
              >
                שדות המסומנים בכוכב הם שדות חובה
              </NewText>
            </StyledTextWrapper>
            <StyledButtonWrapper>
              <ButtonWithArrow
                disabled={isLoading}
                title={"חיזרו אליי"}
                largeDesktopFontSize={20}
                largeDesktopHeight={48}
                largeDesktopWidth={156}
                desktopFontSize={14}
                desktopWidth={156}
                desktopHeight={40}
                mobileFontSize={14}
                mobileWidth={124}
                mobileHeight={40}
                smallDesktopFontSize={16}
                smallDesktopHeight={40}
                smallDesktopWidth={124}
                type="submit"
              />
            </StyledButtonWrapper>
            {RECAPTCHA_KEY && (
              <ReCAPTCHA
                ref={reCaptchaRef}
                sitekey={RECAPTCHA_KEY}
                size="invisible"
                hl="iw"
                tabIndex={-1}
              />
            )}
          </StyledFiedlset>
        </form>
      </StyledFormDiv>
      <ContactUsState
        isOpen={isOpen}
        isSuccess={isSuccess}
        setIsOpen={setIsOpen}
      />
    </>
  )
}

export default PazomatForm
